"use client";

import React, { useMemo } from "react";
import { Briefcase, User } from "lucide-react";

import type { Contracts } from "@/src/gql/graphql";
import type { Deal } from "@/lib/types/database/deals";
import type { Contacts } from "@/src/gql/graphql";

import { UniversalEntityPage } from "@/components/shared/UniversalEntityPage";
import { CompanyContacts } from "@/features/companies/components/CompanyContacts";
import { DealCommunications } from "@/features/deals/components/DealCommunications";
import { useDealHooks } from "@/features/deals/hooks/use-deals-hooks";
import { useContactHooks } from "@/lib/hooks/api/shared/contacts-graphql";
import { getEntityTableConfig } from "@/lib/entity-registry";

const DEALS_CONFIG = getEntityTableConfig<Deal>("deals");
const CONTACTS_CONFIG = getEntityTableConfig<Contacts>("contacts");

function EmptyParent({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}) {
  return (
    <div className="mt-4 flex flex-col items-center justify-center gap-2 rounded-lg border bg-card p-8 text-center">
      <Icon className="h-8 w-8 text-muted-foreground" />
      <h3 className="text-base font-semibold text-card-foreground">{title}</h3>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

/**
 * The single deal this contract is attached to. Reuses the universal deals
 * table filtered by `id` (one row) so it inherits row actions + right-click
 * (ENTITY_TABLE_ROW_ACTIONS). Tab is hidden when there is no deal_id.
 */
export function ContractDealDisplay({ item: contract }: { item: Contracts }) {
  const dealHooks = useDealHooks();
  const dealId =
    typeof contract.deal_id === "string" && contract.deal_id.length > 0 ? contract.deal_id : null;

  const customFilters = useMemo(() => ({ id: dealId ?? "" }), [dealId]);

  if (!dealId) {
    return (
      <EmptyParent
        icon={Briefcase}
        title="No deal attached"
        description="This contract is not tied to a deal."
      />
    );
  }

  return (
    <UniversalEntityPage
      entityName="deals"
      config={DEALS_CONFIG}
      hooks={dealHooks}
      filterPreset="all"
      title="Deal"
      description="The deal this contract is attached to"
      storageKey={`contract-${contract.id}-deal-column-preferences`}
      customFilters={customFilters}
      showHeader={false}
    />
  );
}

/**
 * The contact / recipient this contract is with. Reuses the universal contacts
 * table filtered by `id` (single parent) for row actions + right-click.
 */
export function ContractContactDisplay({ item: contract }: { item: Contracts }) {
  const contactHooks = useContactHooks();
  const contactId =
    typeof contract.contact_id === "string" && contract.contact_id.length > 0
      ? contract.contact_id
      : null;

  const customFilters = useMemo(() => ({ id: contactId ?? "" }), [contactId]);

  if (!contactId) {
    return (
      <EmptyParent
        icon={User}
        title="No linked contact"
        description={
          contract.recipient_email
            ? `Sent to ${contract.recipient_name ?? contract.recipient_email} (not a CRM contact).`
            : "This contract is not linked to a CRM contact."
        }
      />
    );
  }

  return (
    <UniversalEntityPage
      entityName="contacts"
      config={CONTACTS_CONFIG}
      hooks={contactHooks}
      filterPreset="all"
      title="Contact"
      description="The contact this contract is with"
      storageKey={`contract-${contract.id}-contact-column-preferences`}
      customFilters={customFilters}
      showHeader={false}
    />
  );
}

/**
 * Contacts at the contract's company (single-parent company tab body). The
 * company details card itself is rendered by the Overview tab; here we surface
 * the company roster via the reusable CompanyContacts component.
 */
export function ContractCompanyContacts({ companyId }: { companyId: string }) {
  return (
    <div>
      <h3 className="mb-4 text-lg font-semibold text-card-foreground">Contacts at this company</h3>
      <CompanyContacts companyId={companyId} />
    </div>
  );
}

/**
 * Emails / calls / meetings for the contract's company or contact. Reuses the
 * deal communications panel (company_id-scoped OR contact in company roster);
 * the contract id only namespaces column preferences.
 */
export function ContractCommunicationsDisplay({ item: contract }: { item: Contracts }) {
  const companyId =
    typeof contract.company_id === "string" && contract.company_id.length > 0
      ? contract.company_id
      : "";

  if (!companyId) {
    return (
      <EmptyParent
        icon={User}
        title="No company linked"
        description="Link a company to this contract to view its communications."
      />
    );
  }

  return (
    <DealCommunications
      dealId={contract.id}
      companyId={companyId}
      companyDisplayName={contract.companies?.name ?? "linked company"}
      className="min-h-[200px]"
    />
  );
}
